import { FaBars, FaChevronDown } from "react-icons/fa";
import { useState, useRef, useEffect } from "react";
import Button from "../ui/Button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import type { TopbarProps } from "../../types/topbar.ts";

export default function Topbar({ onMenuClick }: TopbarProps) {
    const navigate = useNavigate();
    const { user, isAuthenticated, logout } = useAuth();
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleLogout = () => {
        logout();
        setShowDropdown(false);
        navigate("/login");
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Get user initials for avatar
    const getUserInitials = () => {
        if (!user) return 'U';
        const firstInitial = user.firstName?.charAt(0).toUpperCase() || '';
        const lastInitial = user.lastName?.charAt(0).toUpperCase() || '';
        return firstInitial + lastInitial || 'U';
    };

    return (
        <header className="w-full border-b border-gray-100 bg-white/80 backdrop-blur-md px-4 md:px-8 h-20 flex flex-shrink-0 items-center justify-between sticky top-0 z-20 transition-all">
            <div className="flex items-center gap-2 md:gap-3">
                <button
                    onClick={onMenuClick}
                    className="p-2 rounded-xl md:hidden hover:bg-gray-100 transition-colors"
                >
                    <FaBars className="text-gray-600" />
                </button>
                <div className="hidden md:block">
                    <h2 className="text-xl font-bold text-gray-800 tracking-tight transition-all">
                        Welcome back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-fuchsia-600">{user?.firstName || 'User'}</span>!
                    </h2>
                </div>
            </div>

            <div className="flex items-center gap-6">


                {/* User profile badge with dropdown */}
                {isAuthenticated && user ? (
                    <div className="relative" ref={dropdownRef}>
                        <div 
                            className="flex items-center gap-3 bg-white border border-gray-100 rounded-2xl py-1.5 px-2.5 pr-4 cursor-pointer hover:shadow-md hover:border-violet-200 transition-all duration-300 group"
                            onClick={() => setShowDropdown(!showDropdown)}
                        >
                            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-violet-600 to-fuchsia-600 flex items-center justify-center text-white text-xs font-bold shadow-sm transition-transform group-hover:scale-105">
                                {getUserInitials()}
                            </div>

                            <div className="flex flex-col">
                                <span className="text-sm font-bold text-gray-800 group-hover:text-violet-600 transition-colors">
                                    {user.firstName}
                                </span>
                                <span className="text-[10px] font-bold text-violet-600 uppercase tracking-tighter">
                                    {user.isAdmin ? "Unlimited Credits" : `${(user.creditsRemaining || 0).toLocaleString()} Credits`}
                                </span>
                            </div>

                            <FaChevronDown 
                                className={`text-[10px] text-gray-400 transition-transform duration-300 ${
                                    showDropdown ? 'rotate-180' : ''
                                }`} 
                            />
                        </div>

                        {/* Dropdown Menu */}
                        {showDropdown && (
                            <div className="absolute right-0 mt-4 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-violet-600 text-lg font-bold">
                                        {getUserInitials()}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-gray-900 truncate">{user.firstName} {user.lastName}</p>
                                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                    </div>
                                </div>
                                
                                <div className="p-2">
                                    <button
                                        onClick={() => {
                                            setShowDropdown(false);
                                            navigate("/settings");
                                        }}
                                        className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-violet-50 hover:text-violet-600 rounded-xl transition-all cursor-pointer"
                                    >
                                        Profile & Settings
                                    </button>
                                    
                                    <button
                                        onClick={() => {
                                            setShowDropdown(false);
                                            navigate("/billing");
                                        }}
                                        className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-violet-50 hover:text-violet-600 rounded-xl transition-all cursor-pointer"
                                    >
                                        Billing
                                    </button>
                                </div>
                                
                                <div className="border-t border-gray-50 m-2 pt-2">
                                    <button
                                        onClick={handleLogout}
                                        className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm font-semibold text-red-500 hover:bg-red-50 rounded-xl transition-all cursor-pointer"
                                    >
                                        Logout
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center gap-3">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate("/login")}
                            className="rounded-xl"
                        >
                            Login
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => navigate("/signup")}
                            className="bg-gradient-to-r from-violet-600 to-fuchsia-600 border-0 rounded-xl shadow-lg ring-fuchsia-100 hover:ring-4 transition-all"
                        >
                            Get Started
                        </Button>
                    </div>
                )}
            </div>
        </header>
    );
}